import React from 'react';
import { makeStyles } from '@material-ui/core';
import lodashGet from 'lodash/get';
import { addCommas } from '../../../utils';
import { COLOR_YELLOW } from '../../colors';
import { skins } from '../skins';
import { useFormatMessage } from 'react-intl-hooks';
import { HistoricalTrend } from '../HistoricalTrend';

const COPYRIGHT_YEAR = new Date().getFullYear();
const useStyles = makeStyles((theme)=> ({
    area: {
        fontSize: '48px',
        lineHeight: '68px',
        fontWeight: 500,
    },
    highlightContainer: {
        fontSize: '76px',
        lineHeight: '76px',
        fontWeight: 700,
        display: 'inline-block',
        padding: '16px 32px 20px',
        position: 'relative',
    },
    highlight: {
        width: '100%',
        height: '76px',
        position: 'absolute',
        transform: 'matrix(1, -0.02, 0.02, 1, 0, 0)',
        bottom: '10px',
        left: 0,
        zIndex: 1,
    },
    highlightValue: {
        position: 'relative',
        zIndex: 2,
    },
    statsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        maxWidth: '714px',
        minWidth: (props)=> props.language === 'es' ? '100%' : 'auto',
        marginTop: '16px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    statsRowItem: {
        maxWidth: 270,
    },
    statsRowItemValue: {
        minWidth: '200px',
        fontSize: '48px',
        lineHeight: '68px',
        fontWeight: 500,
    },
    compliance: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '14px',
        lineHeight: '20px',
        marginTop: '24px',
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'center',
        width: '340px',
        textAlign: 'end',
    },
    disclaimerText: {
        marginRight: '16px',
    },
    copyright: {
        width: '325px',
        textAlign: 'start',
    }
}));

export default function StatsHighlight(props) {
    const {
        skin,
        medianDom: median_dom,
        medianDomTrend: median_dom_mm,
        medianPrice: median_price,
        medianPriceTrend: median_price_mm,
        activeListingsCount: active_listings_count,
        activeListingsCountTrend: active_listings_count_mm,
        newListingsCount: new_listings_count,
        fromMonth,
        toMonth,
        area,
        month,
    } = props || {};
    const classes = useStyles(props);
    const t = useFormatMessage();
    return (
        <>
            <div className={classes.month}>
                {t({ id: 'title', defaultMessage: 'Local market report for' })} {t({ id: month })}
            </div>

            <div className={classes.area}>
                {area}
            </div>
            <div className={classes.highlightContainer}>
                <div className={classes.highlight}
                    style={{
                        backgroundColor: lodashGet(skins, `${skin}.HIGHLIGHT_COLOR`, COLOR_YELLOW)
                    }}></div>
                <div className={classes.highlightValue}>
                    {median_price}
                </div>
            </div>
            <HistoricalTrend
                value={median_price_mm}
                fromMonth={fromMonth}
                toMonth={toMonth}
                size="large"
            />
            <div dangerouslySetInnerHTML={{__html: t({ id: 'median_price' }, { separator: ' ' })}}></div>

            <div className={classes.statsRow}>
                <div className={classes.statsRowItem}>
                    <div className={classes.statsRowItemValue}>
                        {`${active_listings_count > 0 ? addCommas(active_listings_count) : '-'}`}

                        <HistoricalTrend
                            value={active_listings_count_mm}
                            fromMonth={fromMonth}
                            toMonth={toMonth}
                            size="small"
                        />
                    </div>
                    <div dangerouslySetInnerHTML={{__html: t({ id: 'active_listings' }, { separator: '<br/>' })}}></div>
                </div>

                <div className={classes.statsRowItem}>
                    <div className={classes.statsRowItemValue}>
                        {`${new_listings_count > 0 ? addCommas(new_listings_count) : '-'}`}
                    </div>
                    <div dangerouslySetInnerHTML={{__html: t({ id: 'new_listings' }, { separator: '<br/>' })}}></div>
                </div>

                <div className={classes.statsRowItem}>
                    <div className={classes.statsRowItemValue}>
                        {`${median_dom > 0 ? addCommas(median_dom) : '-'}`}

                        <HistoricalTrend
                            value={median_dom_mm}
                            fromMonth={fromMonth}
                            toMonth={toMonth}
                            size="small"
                        />
                    </div>
                    <div dangerouslySetInnerHTML={{__html: t({ id: 'median_dom' }, { separator: '<br/>' })}}></div>
                </div>
            </div>

            <div className={classes.compliance} style={{ color: lodashGet(skins, `${skin}.COMPLIANCE_COLOR`) }}>
                <div className={classes.copyright}>
                    <img src={lodashGet(skins, `${skin}.LR_LOGO_PATH`)} height={16} alt="LR Logo" />
                    <div>
                        &copy; ListReports {COPYRIGHT_YEAR}, {t({ id: 'copyright' })}
                    </div>
                </div>
                <div className={classes.disclaimer}>
                    <div className={classes.disclaimerText}>{t({ id: 'disclaimer' })}</div>
                    <img src={lodashGet(skins, `${skin}.EQUAL_HOUSING_LOGO_PATH`)} height={24} alt="Equal Housing logo" />
                </div>
            </div>
        </>
    );
}
